import { ref } from 'vue'
import { defineStore } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import { getUnreadChecksCount, toggleChecks as toggleChecksApi } from '../api'

export const useSfAssistantChecksReadStatus = defineStore(
  'assistantChecksReadStatus',
  () => {
    const checkReadStatus = ref<Record<string, boolean>>({})
    const checkReadByUsers = ref<Record<string, string[]>>({})
    const unreadCount = ref(0)

    const fetchUnreadCount = async (workflowId?: number): Promise<void> => {
      try {
        const data = await getUnreadChecksCount(workflowId)
        if (data) unreadCount.value = data.count
      } catch (error) {
        SfLog.warn('Failed to load unread checks count', {
          extra: { ...window.Silverfin.jsonParams, error },
        })
      }
    }

    const setReadStatusChecks = (checkIds: string[]): void => {
      checkReadStatus.value = checkIds.reduce((acc, checkId) => {
        acc[checkId] = true
        return acc
      }, {} as Record<string, boolean>)
    }

    const setReadByUsersChecks = (
      usersPerCheckId: Record<string, string[]>
    ): void => {
      checkReadByUsers.value = usersPerCheckId
    }

    const markChecksAsRead = async (checkIds: string[]): Promise<void> => {
      // Update the read status of the checks/counter immediately
      // so we do not wait for the loading to finish
      const initialReadStatus = { ...checkReadStatus.value }
      const newlyReadCount = checkIds.filter(
        (id) => !checkReadStatus.value[id]
      ).length

      checkIds.forEach((checkId) => {
        checkReadStatus.value[checkId] = true
      })
      unreadCount.value = Math.max(0, unreadCount.value - newlyReadCount)

      try {
        await toggleChecksApi({
          type: 'read',
          checkIds,
        })
      } catch (error) {
        // Revert the read status and counter immediately in case of an error
        checkReadStatus.value = initialReadStatus
        unreadCount.value += newlyReadCount

        SfLog.warn('Failed to execute markChecksAsRead()', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
        throw error
      }
    }

    const toggleCheck = async (checkId: string): Promise<void> => {
      // Update the read status of the check/counter immediately
      // so we do not wait for the loading to finish
      const wasRead = checkReadStatus.value[checkId]
      checkReadStatus.value[checkId] = !wasRead

      if (wasRead) {
        // Marking as unread - increment count
        unreadCount.value += 1
      } else {
        // Marking as read - decrement count
        unreadCount.value = Math.max(0, unreadCount.value - 1)
      }

      try {
        await toggleChecksApi({
          type: wasRead ? 'unread' : 'read',
          checkIds: [checkId],
        })
      } catch (error) {
        // Revert the read status and counter immediately in case of an error
        checkReadStatus.value[checkId] = wasRead
        if (wasRead) {
          unreadCount.value -= 1
        } else {
          unreadCount.value += 1
        }
        SfLog.warn('Failed to execute toggleCheck()', {
          extra: { error, ...window.Silverfin.jsonParams },
        })
      }
    }

    return {
      checkReadStatus,
      checkReadByUsers,
      unreadCount,
      setReadStatusChecks,
      setReadByUsersChecks,
      fetchUnreadCount,
      markChecksAsRead,
      toggleCheck,
    }
  }
)
